import { CSVRow, loadCSVFromData } from "../../../lib/csvLoader";
import { ProfilerProductId, ProfilerProduct } from "../types/ProfilerProduct";
import { FragranceProfilerResults } from "../types/FragranceProfilerResults";
import { beautyProfilerProductRepository } from "./ProductRepository";
import { AnswerOptionId } from "../types/AnswerOption"; // Import the id type for clarity
import { fragranceProfilerData } from "../../../data/fragranceProfiler";

/**
 * Repository for handling Fragrance Profiler result mappings.
 * Loads from `/data/fragranceProfiler.csv` and maps answerOptionId combinations to FragranceProfilerResults.
 * Keys are normalized sets of AnswerOptionId, as a sorted string.
 */
export class FragranceProfilerRepository {
  private resultMap: Map<string, { answerOptionIds: Set<AnswerOptionId>; results: FragranceProfilerResults }> = new Map();
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Ensure products are loaded before mapping result rows to product objects
      await beautyProfilerProductRepository.initialize();

      const rows: CSVRow[] = await loadCSVFromData(fragranceProfilerData);
      for (const row of rows) {

        const answerOptionIds: AnswerOptionId[] = row.answerOptions
          .split(",")
          .map((s: string) => parseInt(s.trim()))
          .filter((id: number) => id !== 41 && id !== 42)
          .sort((a, b) => a - b); // normalization

        const answerOptionIdSet = new Set(answerOptionIds);

        // The key is the sorted, joined string, e.g. "43,47,51"
        const key = answerOptionIds.join(",");

        // Parse product ids from columns
        const getId = (col: string) => parseInt(row[col]);
        // Product ids from csv columns
        const feticheId = getId("fetiche");
        const opulenceId = getId("opulence");
        const sensualityId = getId("sensuality");
        const mysteryId = getId("mystery");
        const eleganceId = getId("elegance");
        const alsoLikeFeticheId = getId("alsoLikeFetiche");
        const alsoLikeLoubiworldId = getId("alsoLikeLoubiworld");

        // Use product repository to get product objects
        const fetiche = beautyProfilerProductRepository.getProductById(feticheId);
        const opulence = beautyProfilerProductRepository.getProductById(opulenceId);
        const sensuality = beautyProfilerProductRepository.getProductById(sensualityId);
        const mystery = beautyProfilerProductRepository.getProductById(mysteryId);
        const elegance = beautyProfilerProductRepository.getProductById(eleganceId);
        const alsoLikeFetiche = beautyProfilerProductRepository.getProductById(alsoLikeFeticheId);
        const alsoLikeLoubiworld = beautyProfilerProductRepository.getProductById(alsoLikeLoubiworldId);

        this.resultMap.set(key, {
          answerOptionIds: answerOptionIdSet,
          results: {
            feticheFragrance: fetiche,
            opulenceFragrance: opulence,
            sensualityFragrance: sensuality,
            mysteryFragrance: mystery,
            eleganceFragrance: elegance,
            alsoLikeFeticheFragrance: alsoLikeFetiche,
            alsoLikeLoubiworldFragrance: alsoLikeLoubiworld,
          },
        });
      }
      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize FragranceProfilerRepository:", error);
      throw error;
    }
  }

  /**
   * Given an array of answer option ids (numbers), retrieve the corresponding FragranceProfilerResults.
   * Answer option ids may be in any order.
   */
  getResultsByAnswerOptionIds(answerOptionIds: number[]): FragranceProfilerResults {
    if (!this.isInitialized) {
      throw new Error(
        "FragranceProfilerRepository not initialized. Call initialize() first."
      );
    }
    // Ignore 41/42 as they are non-discriminative for result mapping
    const normalized = [...answerOptionIds]
      .filter((id) => id !== 41 && id !== 42)
      .sort((a, b) => a - b);
    const lookupKey = normalized.join(",");
    const mapEntry = this.resultMap.get(lookupKey);
    if (!mapEntry) {
      throw new Error(
        `Fragrance profiler results for answerOptionIds ${lookupKey} not found`
      );
    }
    // returns just the FragranceProfilerResults object
    return mapEntry.results;
  }

  /**
   * Optionally, retrieve the Set of answer option ids by lookup key (added for completeness)
   */
  getAnswerOptionIdSetByIds(answerOptionIds: number[]): Set<AnswerOptionId> | undefined {
    const lookupKey = [...answerOptionIds].sort((a, b) => a - b).join(",");
    return this.resultMap.get(lookupKey)?.answerOptionIds;
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

export const fragranceProfilerRepository = new FragranceProfilerRepository();
